import React, { useRef, useState, useEffect, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { useNavigate } from "react-router-dom";
import { setCurrentAccent } from "./accentBus";
import { setSharedPlaying } from "./playingBus";
import { registerSwiperControls } from "./swiperControl";
import SlideToolbar from "./SlideToolbar";
import SocialQrModal from "./SocialQrModal";
import {
  FaInstagram as FaInstagramRaw,
  FaWhatsapp as FaWhatsappRaw,
  FaYoutube as FaYoutubeRaw,
  FaFacebookF as FaFacebookRaw,
} from "react-icons/fa";

import HTLDSlide1 from "./1_htld";
import HTLDSlide2 from "./2_upanishad";
import HTLDSlide3 from "./3_mindStates";
import HTLDSlide4 from "./4_dreamState";
import HTLDSlide5 from "./5_memory";
import HTLDSlide6 from "./6_sleepCycle";
import HTLDSlide6a from "./6a_psychosomaticDiseases";
import HTLDSlide6b from "./6b_nostrilSleepCycle";
import HTLDSlide7 from "./7_brainWaves";
import HTLDSlide8 from "./8_adhamaPranayama";
import HTLDSlide9 from "./9_nadiShuddhi";
import HTLDSlide10 from "./10_chandraBhedana";
import HTLDSlide10a from "./10a_bhramari";
import { accent10a } from "./slide10a.data";
import HTLDSlide11 from "./11_yogaNidra";
import SimpleView from "./simpleView";
import HTLDSlide7b from "./7b_mindBody";
import HTLDSlide7c from "./7c_eightLimbs";
import Meditation from "./10b_meditation";

import { setCurrentMode } from "./modeBus";
import { FONT_BODY } from "theme/fonts";
import { useLang } from "i18n/lang";
import { useUi } from "i18n/ui";

// react-icons v5's IconType returns ReactNode, which React 19's stricter JSX
// component typing rejects — cast once here rather than at every call site.
const FaInstagram = FaInstagramRaw as unknown as React.FC<{ size?: number }>;
const FaWhatsapp = FaWhatsappRaw as unknown as React.FC<{ size?: number }>;
const FaYoutube = FaYoutubeRaw as unknown as React.FC<{ size?: number }>;
const FaFacebook = FaFacebookRaw as unknown as React.FC<{ size?: number }>;

// TODO: replace with the real Instagram handle and WhatsApp number.
const SOCIAL_LINKS = {
  instagram: "https://instagram.com/yogyism",
  whatsapp: "https://chat.whatsapp.com/EIASdNv9ujzFKnGFmPfxBp",
  youtube: "https://www.youtube.com/@Yogyism",
  facebook: "https://www.facebook.com/profile.php?id=61591601232638",
};

// ── Safe autoplay helpers ─────────────────────────────────────────────────────
function apStop(sw: SwiperType | null | undefined) {
  try {
    if (sw && sw.autoplay && typeof (sw.autoplay as any).stop === "function")
      (sw.autoplay as any).stop();
  } catch (_) {}
}

function apStart(sw: SwiperType | null | undefined) {
  try {
    if (sw && sw.autoplay && typeof (sw.autoplay as any).start === "function")
      (sw.autoplay as any).start();
  } catch (_) {}
}

function apTimeLeft(sw: SwiperType | null | undefined): number {
  try {
    const t = (sw?.autoplay as any)?.timeLeft;
    return typeof t === "number" && t > 0 ? t : 0;
  } catch (_) {
    return 0;
  }
}

// Convert "#378add" → "55,138,221" for the toolbar's rgba() borders.
function hexToRgb(hex: string): string {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;

  const n = parseInt(full, 16);

  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

// ── HOOK: detect mobile ─────────────────────────────────────────────────────
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);

    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);

  return isMobile;
}

const DELAY = 10000;
const ACCENT = "white";

type EyeStateType = "open" | "half" | "closed" | "dreaming";
const eyeState: EyeStateType = "open";

// ── Slides are described as factory functions ────────────────────────────────
type SlideDef = {
  label: string;
  render: () => React.ReactNode;
};

const ALL_SLIDES: SlideDef[] = [
  { label: "Dreamfulness", render: () => <HTLDSlide1 /> },
  { label: "Mandukya", render: () => <HTLDSlide2 /> },
  { label: "Mind States", render: () => <HTLDSlide3 /> },
  { label: "Dream State", render: () => <HTLDSlide4 /> },
  { label: "Memory & Dreams", render: () => <HTLDSlide5 /> },
  { label: "Psychosomatic", render: () => <HTLDSlide6a /> },
  { label: "Sleep Cycle", render: () => <HTLDSlide6 /> },
  { label: "Nostril & Sleep", render: () => <HTLDSlide6b /> },
  { label: "Brain Waves", render: () => <HTLDSlide7 /> },
  { label: "Mind Body", render: () => <HTLDSlide7b /> },
  { label: "8 Limbs", render: () => <HTLDSlide7c /> },
  { label: "Adhama", render: () => <HTLDSlide8 /> },
  { label: "Nadi Shuddhi", render: () => <HTLDSlide9 /> },
  { label: "Chandra Bhedana", render: () => <HTLDSlide10 /> },
  { label: "Bhramari", render: () => <HTLDSlide10a /> },
  { label: "Meditation", render: () => <Meditation /> },
  { label: "Yoga Nidra", render: () => <HTLDSlide11 /> },
  { label: "Session", render: () => <SimpleView eyeState={eyeState} /> },
];

const PRACTICE_START = 11;

const NEIGHBOR_WINDOW = 1;

const SLIDE_ACCENTS = [
  "#378add", // 0  Dreamfulness
  "#7f77dd", // 1  Mandukya Upanishad
  "#4fc3f7", // 2  Mind States
  "#d4537e", // 3  Dream State
  "#9b72dd", // 4  Memory & Dreams
  "#d4537e", // 5  Psychosomatic
  "#38b5b5", // 6  Sleep Cycle
  "#c76a94", // 7  Nostril & Sleep
  "#4fc3f7", // 8  Brain Waves
  "#c090e0", // 9  Mind Body
  "#38b5b5", // 10 The Eight Limbs
  "#e07b39", // 11 Adhama Pranayama
  "#3ecb8a", // 12 Nadi Shuddhi
  "#7b8ef0", // 13 Chandra Bhedana
  accent10a, // 14 Bhramari
  "#4bb8b0", // 15 Meditation
  "#c090e0", // 16 Yoga Nidra
  "#378add", // 17 Session
];

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE SECTION COLOR CODING
   ═══════════════════════════════════════════════════════════════════════════ */

type SlideSection = "theory" | "practice" | "lucid";

const SLIDE_SECTIONS: SlideSection[] = [
  // THEORY
  "theory", // 0  Dreamfulness
  "theory", // 1  Mandukya
  "theory", // 2  Mind States
  "theory", // 3  Dream State
  "theory", // 4  Memory & Dreams
  "theory", // 5  Psychosomatic
  "theory", // 6  Sleep Cycle
  "theory", // 7  Nostril & Sleep
  "theory", // 8  Brain Waves
  "theory", // 9  Mind Body
  "theory", // 10 8 Limbs

  // PRACTICE
  "practice", // 11 Adhama
  "practice", // 12 Nadi Shuddhi
  "practice", // 13 Chandra Bhedana
  "practice", // 14 Bhramari
  "practice", // 15 Meditation
  "practice", // 16 Yoga Nidra

  // LUCID DREAMING
  "lucid", // 17 Session
];

// Bottom labels are all silver; the mark around each label shows its section.
const LABEL_SILVER = "200,210,220";
const LABEL_SILVER_ACTIVE = "#eef3f8";
type SectionMark = "none" | "underline" | "dashed-box";
const SECTION_MARK: Record<SlideSection, SectionMark> = {
  theory: "none",
  practice: "underline",
  lucid: "dashed-box",
};

// Marks are drawn in the text colour. Every label takes the same 4px above
// and below its text (a box uses 1px border + 3px padding), so all titles
// share one baseline.
const MARK_SPACE = 4;
function sectionMark(mark: SectionMark): React.CSSProperties {
  if (mark === "dashed-box") {
    return {
      display: "inline-block",
      padding: `${MARK_SPACE - 1}px 4px`,
      border: "1px dashed currentColor",
      borderRadius: 3,
    };
  }
  return {
    display: "inline-block",
    paddingTop: MARK_SPACE,
    paddingBottom: MARK_SPACE,
    ...(mark === "underline" && {
      backgroundImage: "linear-gradient(currentColor, currentColor)",
      backgroundSize: "100% 1px",
      backgroundPosition: "0 100%",
      backgroundRepeat: "no-repeat",
    }),
  };
}

const STYLE_ID = "tutorial-swiper-styles";

if (!document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");

  s.id = STYLE_ID;

  s.textContent = `
    .ttl-bar::-webkit-scrollbar { display: none; }
    .ttl-bar { scrollbar-width: none; -ms-overflow-style: none; }

    .swiper, .swiper-wrapper {
      height: 100% !important;
    }

    .swiper-slide {
      height: 100% !important;
      overflow: hidden;
    }

    .ttl-bar-fade {
      -webkit-mask-image: linear-gradient(
        to right,
        transparent 0,
        black 14px,
        black calc(100% - 14px),
        transparent 100%
      );

      mask-image: linear-gradient(
        to right,
        transparent 0,
        black 14px,
        black calc(100% - 14px),
        transparent 100%
      );
    }

    .yg-ctrl-btn {
      transition:
        transform 0.12s ease,
        box-shadow 0.12s ease,
        opacity 0.12s ease,
        background 0.15s ease,
        border-color 0.15s ease;
    }

    .yg-ctrl-btn:active {
      transform: scale(0.92);
    }

    .yg-mode-pill button {
      transition:
        background 0.18s ease,
        color 0.18s ease,
        box-shadow 0.18s ease;
    }
  `;

  document.head.appendChild(s);
}

type Mode = "all" | "practice";

const TutorialSwiper: React.FC = () => {
  const { lang } = useLang();
  const ui = useUi();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const swiperRef = useRef<SwiperType | null>(null);
  const activeTitleRef = useRef<HTMLButtonElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  const [accentColor, setAccentColor] = useState(ACCENT);

  const isMobile = useIsMobile(640);

  // ── RAF progress bar ─────────────────────────────────────────────────────
  const rafIdRef = useRef<number | null>(null);
  const barStartTimeRef = useRef<number>(0);
  const barFromPctRef = useRef<number>(0);
  const barDurationRef = useRef<number>(DELAY);
  const barFrozenPctRef = useRef<number>(0);

  // ── Guards ────────────────────────────────────────────────────────────────
  const modalOpenRef = useRef(false);
  const intentionalStopRef = useRef<boolean>(false);

  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const remainingMsRef = useRef<number>(DELAY);

  // ── Playing state ─────────────────────────────────────────────────────────
  const [playing, setPlaying] = useState(true);
  const playingRef = useRef(true);

  const modeRef = useRef<Mode>("all");

  const [active, setActive] = useState(0);
  const [mode, setMode] = useState<Mode>("all");
  const [isDevicesReady, setIsDevicesReady] = useState(false);

  // ── Social QR modal state ─────────────────────────────────────────────────
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showInstagram, setShowInstagram] = useState(false);
  const [showYoutube, setShowYoutube] = useState(false);
  const [showFacebook, setShowFacebook] = useState(false);

  useEffect(() => {
    modeRef.current = mode;
    setCurrentMode(mode);
  }, [mode]);

  // ── Active GLOBAL index ──────────────────────────────────────────────────
  const [activeGlobal, setActiveGlobal] = useState(0);

  const slides =
    mode === "practice" ? ALL_SLIDES.slice(PRACTICE_START) : ALL_SLIDES;

  const navigate = useNavigate();

  const toGlobal = useCallback(
    (localIdx: number, m: Mode) =>
      m === "practice" ? localIdx + PRACTICE_START : localIdx,
    [],
  );

  const setPlayingState = useCallback((val: boolean) => {
    playingRef.current = val;
    setPlaying(val);
    setSharedPlaying(val);
  }, []);

  const apStartIfAllowed = useCallback((sw: SwiperType | null | undefined) => {
    if (modeRef.current === "practice") return;
    apStart(sw);
  }, []);

  const shouldMountLive = useCallback(
    (globalIdx: number) => {
      return Math.abs(globalIdx - activeGlobal) <= NEIGHBOR_WINDOW;
    },
    [activeGlobal],
  );

  // ── RAF loop ──────────────────────────────────────────────────────────────
  const rafLoop = useCallback((now: number) => {
    const bar = progressBarRef.current;

    if (!bar) return;

    const elapsed = now - barStartTimeRef.current;

    const t = Math.min(elapsed / barDurationRef.current, 1);

    const newPct = barFromPctRef.current + (100 - barFromPctRef.current) * t;

    bar.style.width = `${newPct.toFixed(3)}%`;

    if (t < 1) {
      rafIdRef.current = requestAnimationFrame(rafLoop);
    } else {
      rafIdRef.current = null;
    }
  }, []);

  const startBar = useCallback(
    (fromPct: number, durationMs: number) => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      barFromPctRef.current = fromPct;
      barDurationRef.current = durationMs;
      barFrozenPctRef.current = fromPct;

      const bar = progressBarRef.current;

      if (bar) {
        bar.style.width = `${fromPct.toFixed(3)}%`;
      }

      rafIdRef.current = requestAnimationFrame((now) => {
        barStartTimeRef.current = now;
        rafLoop(now);
      });
    },
    [rafLoop],
  );

  const freezeBar = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const bar = progressBarRef.current;

    if (!bar) return;

    const computed = parseFloat(getComputedStyle(bar).width);

    const parentW = bar.parentElement
      ? parseFloat(getComputedStyle(bar.parentElement).width)
      : 1;

    const pct = parentW > 0 ? (computed / parentW) * 100 : 0;

    barFrozenPctRef.current = pct;

    bar.style.width = `${pct.toFixed(3)}%`;
  }, []);

  const zeroBar = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    barFrozenPctRef.current = 0;

    const bar = progressBarRef.current;

    if (bar) {
      bar.style.width = "0%";
    }
  }, []);

  const resetBar = useCallback(() => {
    startBar(0, DELAY);
  }, [startBar]);

  // Cleanup
  useEffect(
    () => () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    },
    [],
  );

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current !== null) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  // ── PAUSE ─────────────────────────────────────────────────────────────────
  const pauseSlideshow = useCallback(
    (showModal = true) => {
      if (!playingRef.current) return;

      clearResumeTimeout();
      freezeBar();

      const sw = swiperRef.current;
      const tl = apTimeLeft(sw);

      remainingMsRef.current =
        tl > 0
          ? tl
          : Math.max(100, DELAY * (1 - barFrozenPctRef.current / 100));

      intentionalStopRef.current = true;

      apStop(sw);

      setPlayingState(false);

      if (showModal) {
        setIsModalOpen(true);
      }
    },
    [clearResumeTimeout, freezeBar, setPlayingState],
  );

  // ── RESUME ────────────────────────────────────────────────────────────────
  const resumeSlideshow = useCallback(() => {
    if (playingRef.current) return;

    clearResumeTimeout();

    const remaining =
      remainingMsRef.current > 50 ? remainingMsRef.current : DELAY;

    const fromPct = barFrozenPctRef.current;

    setPlayingState(true);
    setIsModalOpen(false);

    startBar(fromPct, remaining);

    resumeTimeoutRef.current = setTimeout(() => {
      resumeTimeoutRef.current = null;

      const s = swiperRef.current;

      if (!s || s.isEnd) return;

      intentionalStopRef.current = true;

      apStop(s);

      s.slideNext();

      setTimeout(() => {
        resetBar();

        intentionalStopRef.current = false;

        apStartIfAllowed(swiperRef.current);

        setPlayingState(true);
      }, 60);
    }, remaining);
  }, [
    clearResumeTimeout,
    startBar,
    resetBar,
    setPlayingState,
    apStartIfAllowed,
  ]);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    registerSwiperControls(
      () => {
        modalOpenRef.current = true;
        pauseSlideshow(false);
      },
      () => {
        modalOpenRef.current = false;
        resumeSlideshow();
      },
    );
  }, [pauseSlideshow, resumeSlideshow]);

  useEffect(() => {
    const handler = () => {
      modalOpenRef.current = false;
    };

    window.addEventListener("yogyism:modalClosed", handler);

    return () => window.removeEventListener("yogyism:modalClosed", handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) =>
      setAccentColor((e as CustomEvent).detail.accent);

    window.addEventListener("yogyism:slideAccent", handler);

    return () => window.removeEventListener("yogyism:slideAccent", handler);
  }, []);

  useEffect(() => {
    activeTitleRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [active]);

  // Label bar: on desktop, fade the edges only when the labels actually
  // overflow, and let the mouse wheel scroll the bar sideways.
  const labelBarRef = useRef<HTMLDivElement>(null);
  const [labelBarOverflows, setLabelBarOverflows] = useState(false);

  useEffect(() => {
    const el = labelBarRef.current;
    if (!el) return;
    const measure = () => setLabelBarOverflows(el.scrollWidth > el.clientWidth + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      ro.disconnect();
      el.removeEventListener("wheel", onWheel);
    };
  }, [mode]);

  useEffect(() => {
    clearResumeTimeout();

    const sw = swiperRef.current;

    if (!sw) return;

    const wasPlaying = playingRef.current;

    const targetGlobal = mode === "practice" ? PRACTICE_START : 0;

    intentionalStopRef.current = true;

    apStop(sw);

    sw.slideTo(targetGlobal, 0);

    setActive(0);
    setActiveGlobal(targetGlobal);

    const accent = SLIDE_ACCENTS[targetGlobal] ?? ACCENT;

    setCurrentAccent(accent);
    setAccentColor(accent);

    window.dispatchEvent(
      new CustomEvent("yogyism:slideAccent", { detail: { accent } }),
    );

    setTimeout(() => {
      intentionalStopRef.current = false;

      if (mode !== "practice" && wasPlaying) {
        resetBar();
        apStartIfAllowed(swiperRef.current);
        setPlayingState(true);
      } else {
        zeroBar();

        intentionalStopRef.current = true;

        apStop(swiperRef.current);

        intentionalStopRef.current = false;

        setPlayingState(false);
      }
    }, 60);
  }, [mode]);

  useEffect(() => {
    const handler = () => window.dispatchEvent(new Event("yogyism:stop"));

    window.addEventListener("yogyism:stopAudio", handler);

    return () => window.removeEventListener("yogyism:stopAudio", handler);
  }, []);

  useEffect(() => {
    const pause = () => pauseSlideshow(false);

    const resume = () => resumeSlideshow();

    window.addEventListener("yogyism:play", pause);

    window.addEventListener("yogyism:resume", resume);

    return () => {
      window.removeEventListener("yogyism:play", pause);

      window.removeEventListener("yogyism:resume", resume);
    };
  }, [pauseSlideshow, resumeSlideshow]);

  useEffect(() => {
    const handler = () => {
      setMode("practice");
      setIsModalOpen(false);
    };

    window.addEventListener("yogyism:startPractice", handler);

    return () => window.removeEventListener("yogyism:startPractice", handler);
  }, []);

  useEffect(() => {
    const handler = () => setIsDevicesReady(true);

    window.addEventListener("yogyism:devicesReady", handler);

    return () => window.removeEventListener("yogyism:devicesReady", handler);
  }, []);

  useEffect(() => {
    if (isDevicesReady && active === slides.length - 1) {
      navigate("/session");
    }
  }, [isDevicesReady]);

  const togglePlay = useCallback(() => {
    if (playingRef.current) {
      pauseSlideshow(false);
    } else {
      resumeSlideshow();
    }
  }, [pauseSlideshow, resumeSlideshow]);

  // Shared nav helper
  const navTo = useCallback(
    (fn: (sw: SwiperType) => void) => {
      clearResumeTimeout();

      const sw = swiperRef.current;

      if (!sw) return;

      intentionalStopRef.current = true;

      apStop(sw);

      fn(sw);

      setTimeout(() => {
        intentionalStopRef.current = false;

        remainingMsRef.current = DELAY;

        if (playingRef.current) {
          resetBar();
          apStartIfAllowed(sw);
        } else {
          zeroBar();
        }
      }, 60);
    },
    [clearResumeTimeout, resetBar, zeroBar, apStartIfAllowed],
  );

  const goTo = useCallback(
    (i: number) => navTo((sw) => sw.slideTo(toGlobal(i, mode))),
    [navTo, toGlobal, mode],
  );

  const handlePrev = useCallback(() => {
    if (active > 0) {
      navTo((sw) => sw.slidePrev());
    }
  }, [active, navTo]);

  const handleNext = useCallback(() => {
    if (active < slides.length - 1) {
      navTo((sw) => sw.slideNext());
    }
  }, [active, slides.length, navTo]);

  useEffect(() => {
    const goNext = () => {
      if (mode !== "practice") return;

      if (active < slides.length - 1) {
        navTo((sw) => sw.slideNext());
      }
    };

    window.addEventListener("yogyism:next", goNext);

    return () => window.removeEventListener("yogyism:next", goNext);
  }, [mode, active, slides.length, navTo]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#040f18",
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <SlideToolbar accent={accentColor} accentRgb={hexToRgb(accentColor)} />

        {/* Slide viewport */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Swiper
            modules={[Navigation, Keyboard, Autoplay]}
            slidesPerView={1}
            spaceBetween={0}
            loop={false}
            keyboard={{ enabled: true }}
            autoplay={{
              delay: DELAY,
              disableOnInteraction: false,
            }}
            onSwiper={(s: any) => {
              swiperRef.current = s;

              const firstAccent = SLIDE_ACCENTS[0];

              setCurrentAccent(firstAccent);

              window.dispatchEvent(
                new CustomEvent("yogyism:slideAccent", {
                  detail: {
                    accent: firstAccent,
                  },
                }),
              );

              setActiveGlobal(s.activeIndex ?? 0);

              resetBar();
            }}
            onSlideChange={(s: any) => {
              clearResumeTimeout();

              window.dispatchEvent(new Event("yogyism:stop"));

              const globalIdx = s.activeIndex;

              const localIdx =
                mode === "practice" ? globalIdx - PRACTICE_START : globalIdx;

              setActive(Math.max(0, localIdx));

              setActiveGlobal(globalIdx);

              const accent = SLIDE_ACCENTS[globalIdx] ?? ACCENT;

              setCurrentAccent(accent);

              window.dispatchEvent(
                new CustomEvent("yogyism:slideAccent", {
                  detail: { accent },
                }),
              );

              remainingMsRef.current = DELAY;

              if (modeRef.current !== "practice" && playingRef.current) {
                startBar(0, DELAY);
              } else {
                zeroBar();
              }

              // Session slide: auto-start the hypnagogic background in
              // Practice mode only; in All mode it waits for the Play button.
              if (
                modeRef.current === "practice" &&
                globalIdx === ALL_SLIDES.length - 1
              ) {
                setTimeout(
                  () =>
                    window.dispatchEvent(new Event("yogyism:autoplaySession")),
                  400,
                );
              }
            }}
            onAutoplayTimeLeft={(_: any, __: any, pct: number) => {
              if (
                pct <= 0.01 &&
                active === slides.length - 1 &&
                isDevicesReady
              ) {
                navigate("/session");
              }
            }}
            onAutoplayStop={() => {
              if (intentionalStopRef.current) {
                intentionalStopRef.current = false;
                return;
              }

              if (!playingRef.current || modalOpenRef.current) return;

              const sw = swiperRef.current;

              if (!sw) return;

              const target = mode === "practice" ? PRACTICE_START : 0;

              setTimeout(() => {
                sw.slideTo(target, 600);

                setTimeout(() => {
                  resetBar();

                  apStartIfAllowed(swiperRef.current);

                  setActive(0);

                  setPlayingState(true);
                }, 700);
              }, 300);
            }}
            style={{
              width: "100%",
              height: "100%",
              ["--swiper-navigation-color" as string]: ACCENT,
              ["--swiper-navigation-size" as string]: "22px",
            }}
          >
            {ALL_SLIDES.map((slide, i) => (
              <SwiperSlide
                key={i}
                style={{
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {shouldMountLive(i) ? (
                  <React.Fragment key={lang}>{slide.render()}</React.Fragment>
                ) : (
                  <div
                    aria-hidden
                    style={{
                      width: "100%",
                      height: "100%",
                      background: "#040f18",
                    }}
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Control bar */}
        <div
          style={{
            flexShrink: 0,

            background: isMobile ? "rgba(7, 21, 32, 0.86)" : "#071520",

            backdropFilter: isMobile ? "blur(16px) saturate(140%)" : undefined,

            WebkitBackdropFilter: isMobile
              ? "blur(16px) saturate(140%)"
              : undefined,

            borderTop: isMobile
              ? `1px solid rgba(${hexToRgb(accentColor)},0.22)`
              : "1px solid rgba(55,138,221,0.12)",

            borderRadius: isMobile ? "16px 16px 0 0" : 0,

            boxShadow: isMobile ? "0 -8px 28px rgba(0,0,0,0.35)" : "none",

            maxHeight: isMobile ? "22vh" : undefined,

            paddingTop: isMobile ? 6 : 8,

            paddingBottom: isMobile
              ? "max(4px, env(safe-area-inset-bottom))"
              : 8,

            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Mobile drag handle */}
          {isMobile && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 3,
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.16)",
                }}
              />
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: isMobile ? 42 : 48,
              gap: isMobile ? 8 : 10,
              padding: isMobile ? "0 10px" : 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 8 : 10,
                paddingLeft: isMobile ? 0 : 10,
                flexShrink: 0,
              }}
            >
              {mode !== "practice" && (
                <button
                  onClick={togglePlay}
                  aria-label={playing ? "Pause slideshow" : "Play slideshow"}
                  className="yg-ctrl-btn"
                  style={
                    isMobile
                      ? {
                          background: `linear-gradient(145deg, ${accentColor}30, ${accentColor}12)`,
                          border: `1px solid rgba(${hexToRgb(
                            accentColor,
                          )},0.45)`,
                          borderRadius: "50%",
                          width: 34,
                          height: 34,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          flexShrink: 0,
                        }
                      : {
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          flexShrink: 0,
                        }
                  }
                >
                  {playing ? (
                    <svg
                      width={12}
                      height={13}
                      viewBox="0 0 9 10"
                      fill={isMobile ? accentColor : ACCENT}
                    >
                      <rect x="0" y="0" width="3" height="10" rx="0.8" />
                      <rect x="6" y="0" width="3" height="10" rx="0.8" />
                    </svg>
                  ) : (
                    <svg
                      width={12}
                      height={13}
                      viewBox="0 0 9 10"
                      fill={isMobile ? accentColor : ACCENT}
                      style={{
                        marginLeft: isMobile ? 2 : 0,
                      }}
                    >
                      <polygon points="0,0 9,5 0,10" />
                    </svg>
                  )}
                </button>
              )}

              <div
                className="yg-mode-pill"
                style={{
                  display: "flex",
                  alignItems: "center",

                  background: isMobile
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(55,138,221,0.06)",

                  border: isMobile
                    ? `1px solid rgba(${hexToRgb(accentColor)},0.28)`
                    : "1px solid rgb(255, 255, 255)",

                  borderRadius: 12,
                  padding: "3px",
                  height: 24,
                  overflow: "hidden",
                  boxSizing: "border-box",
                  flexShrink: 0,
                }}
              >
                {(["all", "practice"] as Mode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    style={{
                      background: mode === m ? accentColor : "none",

                      border: "none",

                      borderRadius: 10,

                      padding: "0 9px",

                      height: isMobile ? 18 : 20,

                      cursor: "pointer",

                      fontSize: 10,

                      fontWeight: mode === m ? 700 : 500,

                      whiteSpace: "nowrap",

                      boxShadow:
                        isMobile && mode === m
                          ? `0 2px 8px rgba(${hexToRgb(accentColor)},0.45)`
                          : "none",

                      color: mode === m ? "rgb(7, 21, 32)" : "white",
                    }}
                  >
                    {m === "all"
                      ? ui.tutorial.modeAll
                      : isMobile
                        ? ui.tutorial.modePracticeShort
                        : ui.tutorial.modePractice}
                  </button>
                ))}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════
                SLIDE LABELS — COLOR CODED
                Theory     = Blue
                Practice   = Green
                Lucid      = Purple
                ═══════════════════════════════════════════════════════════ */}
            <div
              ref={labelBarRef}
              className={`ttl-bar${isMobile || labelBarOverflows ? " ttl-bar-fade" : ""}`}
              style={{
                flex: 1,
                minWidth: 0,
                // Full bar height: the scroll container clips vertically too,
                // and would otherwise cut off the labels' underlines.
                alignSelf: "stretch",
                overflowX: "auto",
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 8 : 6,
                scrollbarWidth: "none",
                padding: isMobile ? "0 2px" : labelBarOverflows ? "0 12px" : 0,
              }}
            >
              {slides.map((slide, i) => {
                const isActive = i === active;
                const isPast = i < active;

                // ── Label sections (see SLIDE_SECTIONS / SECTION_MARK) ─────────
                // THEORY:          Dreamfulness → 8 Limbs   (no mark)
                // PRACTICE:        Adhama → Yoga Nidra      (underline)
                // LUCID DREAMING:  Session                  (dashed box)
                // All silver: active = brightest, visited = brighter, upcoming = dimmed.
                const section = SLIDE_SECTIONS[toGlobal(i, mode)];
                const labelColor = isActive
                  ? LABEL_SILVER_ACTIVE
                  : `rgba(${LABEL_SILVER},${isPast ? 0.78 : 0.5})`;

                return (
                  <button
                    key={i}
                    ref={isActive ? activeTitleRef : null}
                    onClick={() => goTo(i)}
                    style={{
                      fontFamily: FONT_BODY, flexShrink: 0,

                      background:
                        isMobile && isActive
                          ? `rgba(${hexToRgb(accentColor)},0.14)`
                          : "none",

                      border:
                        isMobile && isActive
                          ? `1px solid rgba(${hexToRgb(accentColor)},0.4)`
                          : "1px solid transparent",

                      borderRadius: isMobile ? 999 : 0,

                      padding: isMobile ? "3px 9px" : 0,

                      cursor: "pointer",

                      fontSize: isMobile ? 11 : 11.5,

                      fontStyle: "normal",

                      whiteSpace: "nowrap",

                      transition:
                        "color 0.15s ease, background 0.15s ease, border-color 0.15s ease",

                      color: labelColor,
                    }}
                  >
                    <span style={sectionMark(SECTION_MARK[section])}>
                      {ui.tutorial.slideLabels[toGlobal(i, mode)] ?? slide.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 8 : 10,
                paddingRight: isMobile ? 0 : 10,
                flexShrink: 0,
              }}
            >
              {isMobile ? (
                <>
                  <button
                    onClick={handlePrev}
                    disabled={active === 0}
                    aria-label={ui.tutorial.previousAria}
                    className="yg-ctrl-btn"
                    style={{
                      background:
                        active === 0
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(255,255,255,0.06)",

                      border: `1px solid rgba(${hexToRgb(accentColor)},${
                        active === 0 ? 0.12 : 0.4
                      })`,

                      borderRadius: "50%",

                      width: 32,
                      height: 32,

                      display: "flex",

                      alignItems: "center",

                      justifyContent: "center",

                      fontSize: 14,
                      lineHeight: 1,

                      cursor: active === 0 ? "default" : "pointer",

                      color:
                        active === 0 ? "rgba(255,255,255,0.25)" : "#ffffff",
                    }}
                  >
                    ‹
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={active === slides.length - 1}
                    aria-label={ui.tutorial.nextAria}
                    className="yg-ctrl-btn"
                    style={{
                      background:
                        active === slides.length - 1
                          ? "rgba(255,255,255,0.03)"
                          : `linear-gradient(145deg, ${accentColor}38, ${accentColor}14)`,

                      border: `1px solid rgba(${hexToRgb(accentColor)},${
                        active === slides.length - 1 ? 0.12 : 0.55
                      })`,

                      borderRadius: "50%",

                      width: 32,
                      height: 32,

                      display: "flex",

                      alignItems: "center",

                      justifyContent: "center",

                      fontSize: 14,
                      lineHeight: 1,

                      cursor:
                        active === slides.length - 1 ? "default" : "pointer",

                      color:
                        active === slides.length - 1
                          ? "rgba(255,255,255,0.25)"
                          : "#ffffff",
                    }}
                  >
                    ›
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handlePrev}
                    disabled={active === 0}
                    style={{
                      background: "none",
                      border: "1px solid rgba(55,138,221,0.18)",
                      borderRadius: 10,
                      padding: "5px 10px",
                      cursor: active === 0 ? "default" : "pointer",
                      color:
                        active === 0 ? "rgba(255,255,255,0.25)" : "#ffffff",
                    }}
                  >
                    {ui.tutorial.prev}
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={active === slides.length - 1}
                    style={{
                      background: "none",
                      border: "1px solid rgba(55,138,221,0.18)",
                      borderRadius: 10,
                      padding: "5px 10px",
                      cursor:
                        active === slides.length - 1 ? "default" : "pointer",
                      color:
                        active === slides.length - 1
                          ? "rgba(255,255,255,0.25)"
                          : "#ffffff",
                    }}
                  >
                    {ui.tutorial.next}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {mode !== "practice" && (
            <div
              style={{
                height: 3,

                background: isMobile
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(55,138,221,0.10)",

                margin: isMobile ? "6px 10px 0" : 0,

                borderRadius: isMobile ? 4 : 0,

                overflow: "hidden",
              }}
            >
              <div
                ref={progressBarRef}
                style={{
                  height: "100%",
                  width: "0%",
                  borderRadius: isMobile ? 4 : 0,

                  background: `linear-gradient(90deg, ${accentColor}80, ${accentColor})`,

                  boxShadow: isMobile
                    ? `0 0 8px rgba(${hexToRgb(accentColor)},0.6)`
                    : "none",
                }}
              />
            </div>
          )}

          {/* Copyright */}
          {isMobile ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "6px 12px 2px",
                minHeight: 16,
              }}
            >
              <div
                style={{
                  fontSize: 8.5,
                  color: "rgba(255,255,255,0.3)",
                  letterSpacing: 0.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  flex: "1 1 auto",
                }}
              >
                © {new Date().getFullYear()} {ui.tutorial.footerShort}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() => setShowInstagram(true)}
                  aria-label="Instagram"
                  className="yg-ctrl-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 21,
                    height: 21,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.85)`,
                    background: "rgba(255,255,255,0.04)",
                    cursor: "pointer",
                  }}
                >
                  <FaInstagram size={10} />
                </button>

                <button
                  onClick={() => setShowWhatsApp(true)}
                  aria-label="WhatsApp"
                  className="yg-ctrl-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 21,
                    height: 21,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.85)`,
                    background: "rgba(255,255,255,0.04)",
                    cursor: "pointer",
                  }}
                >
                  <FaWhatsapp size={11} />
                </button>

                <button
                  onClick={() => setShowYoutube(true)}
                  aria-label="YouTube"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.75)`,
                    background: "transparent",
                    cursor: "pointer",
                    transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.85)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = `rgba(${hexToRgb(
                      accentColor,
                    )},0.75)`;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.35)`;
                  }}
                >
                  <FaYoutube size={40} />
                </button>

                <button
                  onClick={() => setShowFacebook(true)}
                  aria-label="Facebook"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.75)`,
                    background: "transparent",
                    cursor: "pointer",
                    transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.85)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = `rgba(${hexToRgb(
                      accentColor,
                    )},0.75)`;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.35)`;
                  }}
                >
                  <FaFacebook size={20} />
                </button>
              </div>
            </div>
          ) : (
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 12px 0",
                minHeight: 22,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.25)",
                  letterSpacing: 0.3,
                  textAlign: "center",
                }}
              >
                © {new Date().getFullYear()} {ui.tutorial.footerLong}
              </div>

              <div
                style={{
                  position: "absolute",
                  right: 40,
                  top: "70%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <button
                  onClick={() => setShowInstagram(true)}
                  aria-label="Instagram"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.75)`,
                    background: "transparent",
                    cursor: "pointer",
                    transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.85)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = `rgba(${hexToRgb(
                      accentColor,
                    )},0.75)`;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.35)`;
                  }}
                >
                  <FaInstagram size={40} />
                </button>

                <button
                  onClick={() => setShowWhatsApp(true)}
                  aria-label="WhatsApp"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.75)`,
                    background: "transparent",
                    cursor: "pointer",
                    transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.85)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = `rgba(${hexToRgb(
                      accentColor,
                    )},0.75)`;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.35)`;
                  }}
                >
                  <FaWhatsapp size={40} />
                </button>

                <button
                  onClick={() => setShowYoutube(true)}
                  aria-label="YouTube"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.75)`,
                    background: "transparent",
                    cursor: "pointer",
                    transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.85)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = `rgba(${hexToRgb(
                      accentColor,
                    )},0.75)`;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.35)`;
                  }}
                >
                  <FaYoutube size={40} />
                </button>

                <button
                  onClick={() => setShowFacebook(true)}
                  aria-label="Facebook"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: `1px solid rgba(${hexToRgb(accentColor)},0.35)`,
                    color: `rgba(${hexToRgb(accentColor)},0.75)`,
                    background: "transparent",
                    cursor: "pointer",
                    transition: "opacity 0.2s, border-color 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.85)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = `rgba(${hexToRgb(
                      accentColor,
                    )},0.75)`;

                    e.currentTarget.style.borderColor = `rgba(${hexToRgb(
                      accentColor,
                    )},0.35)`;
                  }}
                >
                  <FaFacebook size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Social QR modals ── */}
      <SocialQrModal
        isOpen={showWhatsApp}
        onClose={() => setShowWhatsApp(false)}
        title="Join our WhatsApp Community"
        qrSrc="/image/whatsappQr.png"
        qrAlt="WhatsApp Community QR code"
        scanHint="Scan with your phone's camera or WhatsApp"
        link={SOCIAL_LINKS.whatsapp}
        buttonLabel="Open WhatsApp Web"
        accent={accentColor}
        accentRgb={hexToRgb(accentColor)}
      />

      <SocialQrModal
        isOpen={showInstagram}
        onClose={() => setShowInstagram(false)}
        title="Follow us on Instagram"
        qrSrc="/image/instagramQr.png"
        qrAlt="Instagram profile QR code"
        scanHint="Scan with your phone's camera or Instagram"
        link={SOCIAL_LINKS.instagram}
        buttonLabel="Open Instagram"
        accent={accentColor}
        accentRgb={hexToRgb(accentColor)}
      />

      <SocialQrModal
        isOpen={showYoutube}
        onClose={() => setShowYoutube(false)}
        title="Subscribe on YouTube"
        qrSrc="/image/YtQr.png"
        qrAlt="YouTube channel QR code"
        scanHint="Scan with your phone's camera"
        link={SOCIAL_LINKS.youtube}
        buttonLabel="Open YouTube"
        accent={accentColor}
        accentRgb={hexToRgb(accentColor)}
      />

      <SocialQrModal
        isOpen={showFacebook}
        onClose={() => setShowFacebook(false)}
        title="Follow us on Facebook"
        qrSrc="/image/FbQr.png"
        qrAlt="Facebook page QR code"
        scanHint="Scan with your phone's camera"
        link={SOCIAL_LINKS.facebook}
        buttonLabel="Open Facebook"
        accent={accentColor}
        accentRgb={hexToRgb(accentColor)}
      />
    </>
  );
};

export default TutorialSwiper;
